import { ShoppingCartOutlined, WarningFilled } from "@ant-design/icons";
import { Button, Input, Skeleton } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import {
  useGetProductCategories,
  useGetProductsForScanToOrder,
  useGetRestaurantTableDetails,
  useGetStoreDetailsForOnlineOrder,
} from "../../api/api.get";
import { PillButton } from "../../components/button.component";
import EmptyComponent from "../../components/empty.component";
import { FeaturesItems } from "../../components/featured-items";
import { FryingPanLottieLoading } from "../../components/lottie.viewer.component";
import { OrgDataComponent } from "../../components/org.data.component";
import ProductViewBottomSheet from "../../components/product.view.botomsheet";
import { RowItem } from "../../components/row.item";
import { useAuthContext } from "../../providers/auth.provider";
import { IProductOnline } from "../../types/types";
import { capitalizeWithTrunc } from "../../utils/functions";
import OrderStatus from "../order-status";
import StoCheckout from "./sto-checkout";

export default function ScanToOrderPage() {
  const { id } = useParams();
  const { tableCode } = useAuthContext();
  const [openBottomSheet, setOpenBottomSheet] = useState<IProductOnline>();

  const { data: categoriesData } = useGetProductCategories(id);

  const { data: storeDetailsData, error } =
    useGetStoreDetailsForOnlineOrder(id);

  const [searchText, setSearchText] = useState<string>("");

  const [debouncedProductNameSearchText] = useDebounce(searchText, 300);

  const { data: productsByNameData, isLoading: isSearchLoading } =
    useGetProductsForScanToOrder(id, debouncedProductNameSearchText);

  const { data: tableDetailsData } = useGetRestaurantTableDetails(
    id,
    tableCode,
  );

  const [productsToCheckout, setProductsToCheckout] = useState<
    IProductOnline[]
  >([]);
  const [items, setItems] = useState<IProductOnline[]>([]);

  const navigate = useNavigate();
  const [confirmCheckout, setConfirmCheckout] = useState<boolean>(false);
  const [showOrderStatus, setShowOrderStatus] = useState<boolean>(false);

  useEffect(() => {
    if (productsByNameData) {
      setItems(productsByNameData);
    } else {
    }
  }, [productsByNameData]);

  const handleRemoveItems = (item: IProductOnline) => {
    const _items = [...productsToCheckout];
    const index = _items.findIndex((p) => p._id === item._id);
    _items.splice(index, 1);
    setProductsToCheckout(_items);
  };

  const handleAddItems = (item: IProductOnline) => {
    const existingProductsToCheckout = [...productsToCheckout];
    existingProductsToCheckout.push(item);
    setProductsToCheckout(existingProductsToCheckout);
  };

  useEffect(() => {
    const _error: any = error;
    if (_error) {
      if (_error.status === 404) {
        navigate("/not-found");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  const [showTextOnly, setShowTextOnly] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowTextOnly(window.scrollY > 300); // You can adjust this threshold
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [step, setStep] = useState<number>(0);

  const handleSelectCategory = (categoryName: string) => {
    if (searchText === categoryName) {
      setSearchText("");
    } else {
      setSearchText(categoryName);
    }
  };

  return (
    <div
      className="mx-auto max-w-5xl min-h-screen"
      style={{
        background: storeDetailsData?.bodyBackgroundColor ?? "white",
        color: storeDetailsData?.bodyFontColor ?? "red",
        // Clears the fixed cart bar so the last item stays reachable.
        paddingBottom: "calc(6rem + env(safe-area-inset-bottom))",
      }}
    >
      <ProductViewBottomSheet
        onAddItems={(product) => handleAddItems(product)}
        onClose={() => setOpenBottomSheet(undefined)}
        onRemoveItems={(product) => handleRemoveItems(product)}
        open={!!openBottomSheet}
        productsToCheckout={productsToCheckout}
        product={openBottomSheet}
      />

      <StoCheckout
        items={productsToCheckout}
        storeDetails={storeDetailsData}
        onOK={() => {
          setConfirmCheckout(false);
          setSearchText("");
          setProductsToCheckout([]);
        }}
        onCancel={() => setConfirmCheckout(false)}
        onRemoveItems={(item) => handleRemoveItems(item)}
        open={confirmCheckout}
      />

      <OrderStatus
        tableName={tableDetailsData?.name}
        onCancel={() => setShowOrderStatus(false)}
        open={showOrderStatus}
      />
      <div
        className="my-0 p-5"
        style={{
          background: storeDetailsData?.headerBackgroundColor ?? "white",
          color: storeDetailsData?.headerFontColor ?? "black",
        }}
      >
        <OrgDataComponent
          storeDetailsData={storeDetailsData}
          variant="restaurant"
        />
      </div>

      {/* Table indicator — shown only after Start, above the search bar. */}
      {step === 1 && (
        <div className="px-5 pt-3">
          {tableCode && tableDetailsData && (
            <div
              onClick={() => setShowOrderStatus(true)}
              className="cursor-pointer flex items-center gap-3 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-5 py-3 shadow-lg"
            >
              <div className="flex-none w-11 h-11 rounded-full bg-white/20 grid place-items-center">
                <img src="/assets/icons/chauffeur.png" width={22} alt="table" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-white/80">
                  You&apos;re seated at
                </div>
                <div className="text-lg font-bold leading-tight truncate">
                  {capitalizeWithTrunc(tableDetailsData?.name, 20) ?? "-"}
                </div>
              </div>
              <span className="text-xs font-semibold bg-white/20 rounded-full px-3 py-1 whitespace-nowrap">
                View order
              </span>
            </div>
          )}

          {tableCode && !tableDetailsData && (
            <div
              onClick={() => setShowOrderStatus(true)}
              className="cursor-pointer flex items-center gap-3 rounded-2xl border border-red-300 bg-red-50 text-red-600 px-5 py-3"
            >
              <WarningFilled />
              <span className="font-semibold">
                Table unknown — tap to check status
              </span>
            </div>
          )}

          {!tableCode && (
            <div className="flex items-center gap-3 rounded-2xl border border-red-300 bg-red-50 text-red-600 px-5 py-3">
              <WarningFilled />
              <span className="font-semibold">Table not found</span>
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div
          className="flex flex-row gap-3 p-5"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 999,
            background: storeDetailsData?.bodyBackgroundColor ?? "white",
          }}
        >
          <Input.Search
            allowClear
            placeholder="Search for items..."
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            size="large"
          />
        </div>
      )}

      {/* Body — uses the body background color from the outer container. */}
      <div className="p-5">
        {step === 0 && (
          <div className="flex flex-col my-20 items-center justify-center gap-16">
            <FryingPanLottieLoading />

            <PillButton onClick={() => setStep(1)} title="Start" />
          </div>
        )}

        {step === 1 && (
          <div>
            <div>
              <div
                style={{
                  display: "flex",
                  overflowX: "auto",
                  gap: showTextOnly ? 0 : 16,
                  padding: showTextOnly ? "0px" : "20px 0px",
                  backgroundColor: "",
                }}
              >
                {categoriesData?.map((cat, idx) => (
                  <div
                    key={idx}
                    className="item"
                    onClick={() => handleSelectCategory(cat.name)}
                    style={{
                      height: showTextOnly ? 40 : undefined,
                      padding: 2,
                      fontFamily: showTextOnly ? "sans-serif" : "inherit",
                      borderRadius: showTextOnly ? 0 : 10,
                      backgroundColor:
                        cat.name === searchText
                          ? "lightgrey"
                          : storeDetailsData?.bodyFontColor,
                      color: storeDetailsData?.bodyBackgroundColor ?? "inherit",
                      border: `1px solid ${storeDetailsData?.bodyBackgroundColor ?? "black"}`,
                    }}
                  >
                    {!showTextOnly && (
                      <img
                        src={cat.image ?? "/assets/images/no-picture.svg"}
                        alt="category"
                      />
                    )}

                    <div className="item-title">
                      {capitalizeWithTrunc(cat.name)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {!searchText && (
              <FeaturesItems onClick={(item) => setOpenBottomSheet(item)} />
            )}

            <div className="my-10" style={{}}>
              <Skeleton active loading={isSearchLoading}>
                {items.length === 0 && (
                  <EmptyComponent
                    color={storeDetailsData?.bodyFontColor ?? "inherit"}
                    title="No Items Found. Please change search terms."
                  />
                )}
                {items.length > 0 &&
                  items.map((item: IProductOnline, index: number) => (
                    <RowItem
                      item={item}
                      key={index}
                      fontColor={storeDetailsData?.bodyFontColor}
                      onAddItems={(product) => handleAddItems(product)}
                      onClick={(product) => setOpenBottomSheet(product)}
                      onRemoveItems={(product) => handleRemoveItems(product)}
                      productsToCheckout={productsToCheckout}
                    />
                  ))}
              </Skeleton>
            </div>
          </div>
        )}
      </div>

      {step === 1 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[1000] p-4"
          style={{
            boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.15)",
            paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
            backgroundColor: storeDetailsData?.bodyBackgroundColor ?? "white",
          }}
        >
          <div className="mx-auto max-w-5xl">
            <Button
              type="primary"
              size="large"
              block
              aria-label="Go to checkout"
              onClick={() => setConfirmCheckout(true)}
              disabled={productsToCheckout.length === 0}
              icon={<ShoppingCartOutlined />}
            >
              Checkout · {productsToCheckout.length} items
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
